// src/components/MapboxGL/controls/CityStatusBarControl.js

/**
 * Control que muestra el selector de Zona/Cantón y Tenant en la parte
 * inferior centrada del mapa. Se monta directamente sobre el contenedor
 * del mapa (no en las esquinas de Mapbox) para lograr el centrado.
 */
class CityStatusBarControl {
  constructor(cityName = '', theme = 'auto', options = {}) {
    this.cityName = cityName
    this.theme = theme
    this.options = {
      initialView: options.initialView || null,
      appBarHeight: options.appBarHeight || '0px',
      cities: options.cities || [],
      cantones: options.cantones || [],
      countries: options.countries || [],
      selectedCountry: options.selectedCountry || null,
      currentTenants: options.currentTenants || [],
      selectedTenant: options.selectedTenant || null,
      currentCitySlug: options.currentCitySlug || null,
      onCityChange: options.onCityChange || null,
      onTenantChange: options.onTenantChange || null,
      onCountryChange: options.onCountryChange || null,
      openCityDropdown: options.openCityDropdown || false,
      cityLabel: options.cityLabel || null,
    }
    this._dropdownOpen = false
    this._tenantDropdownOpen = false
    this._countryDropdownOpen = false
  }

  onAdd(map) {
    this._map = map
    if (!this.options.initialView) {
      const c = map.getCenter()
      this.options.initialView = { center: [c.lng, c.lat], zoom: map.getZoom() }
    }

    // Elemento real (visible), montado directamente en el contenedor del mapa
    this._wrapper = document.createElement('div')
    this._wrapper.className = 'city-statusbar-ctrl'
    Object.assign(this._wrapper.style, {
      position: 'absolute',
      top: `calc(${this.options.appBarHeight} + 10px)`,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '2',
      pointerEvents: 'auto',
    })

    this._shadow = this._wrapper.attachShadow({ mode: 'open' })
    this._renderShell()
    this._renderContent()

    map.getContainer().appendChild(this._wrapper)

    // Elemento dummy requerido por la API de Mapbox
    this._dummy = document.createElement('div')
    return this._dummy
  }

  onRemove() {
    if (this._outsideClickHandler) {
      document.removeEventListener('click', this._outsideClickHandler)
      this._outsideClickHandler = null
    }
    if (this._wrapper?.parentNode) this._wrapper.parentNode.removeChild(this._wrapper)
    if (this._dummy?.parentNode) this._dummy.parentNode.removeChild(this._dummy)
    this._map = null
  }

  updateCity(cityName) {
    this.cityName = cityName || ''
    this._renderContent()
  }

  updateTenants(tenants, selectedTenant) {
    const oldSlugs = (this.options.currentTenants || []).map(t => t.slug).join(',')
    const newSlugs = (tenants || []).map(t => t.slug).join(',')
    this.options.currentTenants = tenants || []
    this.options.selectedTenant = selectedTenant || null

    if (oldSlugs !== newSlugs) {
      this._renderContent()
    } else {
      this._renderTenantSelector()
    }
  }

  updateCities(cities, currentCitySlug) {
    this.options.cities = cities || []
    this.options.currentCitySlug = currentCitySlug || null
    this._renderContent()
  }

  updateCityLabel(label) {
    this.options.cityLabel = label || null
    this._renderContent()
  }

  updateOnCityChange(callback) {
    this.options.onCityChange = callback || null
  }

  updateOnTenantChange(callback) {
    this.options.onTenantChange = callback || null
  }

  updateOnCountryChange(callback) {
    this.options.onCountryChange = callback || null
  }

  updateOpenCityDropdown(val) {
    if (this.options.openCityDropdown === val) return
    this.options.openCityDropdown = val
    this._rootEl?.classList.toggle('needs-selection', Boolean(val))
    this._renderContent()
  }

  updateCountries(countries, selectedCountry) {
    this.options.countries = countries || []
    this.options.selectedCountry = selectedCountry || null
    this._renderContent()
  }

  setInitialView(v) {
    if (v?.center && typeof v.zoom === 'number') this.options.initialView = v
  }

  _renderShell() {
    const style = document.createElement('style')
    style.textContent = `
    :host { all: initial; }
    .bar {
      display: inline-flex;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: center;
      gap: 8px;
      box-sizing: border-box;
      border-radius: 999px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      backdrop-filter: blur(4px);
      padding: 5px 10px;
      font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
      font-size: 0.8rem;
      font-weight: 500;
      color: #111;
      white-space: nowrap;
      overflow: visible;
    }

    @media (prefers-color-scheme: dark) {
      .bar {
        background: rgba(25, 25, 30, 0.85);
        color: #f0f0f0;
      }
    }

    .chip {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 6px 10px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.53);
      border: 1px solid rgba(17,24,39,0.10);
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      backdrop-filter: blur(4px);
      color: #111;
      transition: background .25s ease, color .25s ease, border-color .25s ease;
      white-space: nowrap;
    }

    @media (prefers-color-scheme: dark) {
      .chip {
        background: rgba(25, 25, 30, 0.85);
        color: #f0f0f0;
        border-color: rgba(255,255,255,0.12);
      }
    }

    .chip--city { font-weight: 800; }
    .chip--city .name { color: rgb(0, 0, 0); }

    @media (prefers-color-scheme: dark) {
      .chip--city .name { color: #d6e3ff; }
    }

    .chip--clickable {
      cursor: pointer;
      user-select: none;
    }
    .chip--clickable:active { transform: translateY(0); }

    /* Dropdown de ciudades */
    .city-selector {
      position: relative;
      overflow: visible;
    }

    .dropdown {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      min-width: 180px;
      background: rgba(255, 255, 255, 0.95);
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      backdrop-filter: blur(8px);
      overflow: hidden;
      opacity: 0;
      visibility: hidden;
      transform: translateY(8px);
      transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;
      z-index: 2000;
    }

    .dropdown.open {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    @media (prefers-color-scheme: dark) {
      .dropdown { background: rgba(30, 30, 35, 0.95); }
    }

    .dropdown-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 14px;
      cursor: pointer;
      transition: background 0.15s ease;
      color: #333;
      font-size: 0.85rem;
      border: none;
      background: none;
      width: 100%;
      text-align: left;
    }

    @media (prefers-color-scheme: dark) {
      .dropdown-item { color: #e0e0e0; }
    }

    .dropdown-item.active {
      background: rgba(168, 244, 70, 0.2);
      font-weight: 600;
    }

    .dropdown-item .city-icon {
      width: 18px;
      height: 18px;
      opacity: 0.7;
    }

    .dropdown-item.active .city-icon { opacity: 1; }

    .chip--city .chevron {
      margin-left: 2px;
      transition: transform 0.2s ease;
    }

    .chip--city.open .chevron { transform: rotate(180deg); }

    /* Tenant selector */
    .tenant-selector {
      position: relative;
      overflow: visible;
    }

    .chip--tenant {
      font-weight: 700;
      font-size: 0.8rem;
    }

    .chip--generic {
      border-color: #ed6c02 !important;
      color: #ed6c02 !important;
    }

    @media (prefers-color-scheme: dark) {
      .chip--generic {
        border-color: #ffa726 !important;
        color: #ffa726 !important;
      }
    }

    .chip--tenant .tenant-name {
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .chip--tenant .chevron {
      margin-left: 2px;
      transition: transform 0.2s ease;
    }

    .chip--tenant.open .chevron { transform: rotate(180deg); }

    .tenant-dropdown {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      min-width: 200px;
      max-width: 280px;
      background: rgba(255, 255, 255, 0.95);
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      backdrop-filter: blur(8px);
      overflow: hidden;
      opacity: 0;
      visibility: hidden;
      transform: translateY(8px);
      transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;
      z-index: 2000;
    }

    .tenant-dropdown.open {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    @media (prefers-color-scheme: dark) {
      .tenant-dropdown { background: rgba(30, 30, 35, 0.95); }
    }

    .dropdown-header,
    .tenant-dropdown-header {
      padding: 8px 14px 6px;
      font-size: 0.7rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #666;
      border-bottom: 1px solid rgba(0,0,0,0.08);
    }

    @media (prefers-color-scheme: dark) {
      .dropdown-header,
      .tenant-dropdown-header {
        color: #999;
        border-color: rgba(255,255,255,0.08);
      }
    }

    .tenant-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 14px;
      cursor: pointer;
      transition: background 0.15s ease;
      color: #333;
      font-size: 0.82rem;
      border: none;
      background: none;
      width: 100%;
      text-align: left;
    }

    @media (prefers-color-scheme: dark) {
      .tenant-item { color: #e0e0e0; }
    }

    .tenant-item.active {
      background: rgba(168, 244, 70, 0.2);
      font-weight: 600;
    }

    .tenant-item .tenant-icon {
      width: 16px;
      height: 16px;
      opacity: 0.7;
      flex-shrink: 0;
    }

    .tenant-item.active .tenant-icon { opacity: 1; }

    .tenant-item .tenant-label {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .tenant-item--all {
      border-bottom: 1px solid rgba(0,0,0,0.08);
    }

    @media (prefers-color-scheme: dark) {
      .tenant-item--all { border-color: rgba(255,255,255,0.08); }
    }

    .tenant-item--generic {
      border-top: 1px solid rgba(0,0,0,0.08);
    }

    @media (prefers-color-scheme: dark) {
      .tenant-item--generic { border-color: rgba(255,255,255,0.08); }
    }

    .tenant-badge-generic {
      font-size: 10px;
      font-weight: 600;
      padding: 1px 5px;
      border-radius: 4px;
      background: rgba(237, 108, 2, 0.15);
      color: #ed6c02;
      flex-shrink: 0;
      margin-left: 4px;
    }

    @media (prefers-color-scheme: dark) {
      .tenant-badge-generic {
        background: rgba(237, 108, 2, 0.25);
        color: #ffa726;
      }
    }

    /* Country selector */
    .country-selector {
      position: relative;
      overflow: visible;
    }

    .chip--country {
      font-weight: 700;
      font-size: 0.8rem;
      letter-spacing: 0.3px;
    }

    .chip--country .country-iso {
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.5px;
    }

    .chip--country .chevron {
      margin-left: 2px;
      transition: transform 0.2s ease;
    }

    .chip--country.open .chevron { transform: rotate(180deg); }

    .country-dropdown {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      min-width: 160px;
      background: rgba(255, 255, 255, 0.95);
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      backdrop-filter: blur(8px);
      overflow: hidden;
      opacity: 0;
      visibility: hidden;
      transform: translateY(8px);
      transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;
      z-index: 2000;
    }

    .country-dropdown.open {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    @media (prefers-color-scheme: dark) {
      .country-dropdown { background: rgba(30, 30, 35, 0.95); }
    }

    .country-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 14px;
      cursor: pointer;
      transition: background 0.15s ease;
      color: #333;
      font-size: 0.82rem;
      border: none;
      background: none;
      width: 100%;
      text-align: left;
    }

    @media (prefers-color-scheme: dark) {
      .country-item { color: #e0e0e0; }
    }

    .country-item.active {
      background: rgba(168, 244, 70, 0.2);
      font-weight: 600;
    }

    .country-item .country-flag {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
    }

    .country-item .country-label {
      flex: 1;
      font-weight: 600;
      letter-spacing: 0.3px;
    }

    /* Efecto de atención cuando no hay cantón seleccionado */
    @keyframes pulse-glow {
      0%   { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.55), 0 2px 8px rgba(0,0,0,0.15); }
      60%  { box-shadow: 0 0 0 7px rgba(33, 150, 243, 0),  0 2px 8px rgba(0,0,0,0.15); }
      100% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0),    0 2px 8px rgba(0,0,0,0.15); }
    }

    .needs-selection .chip--city {
      border-color: rgba(33, 150, 243, 0.6);
      animation: pulse-glow 2s ease-out infinite;
    }

    /* Pantallas pequeñas (móviles < 550px): solo iconos en país y ciudad */
    @media (max-width: 549px) {
      .chip--country .country-iso,
      .chip--city .name {
        display: none;
      }
      .chip--country,
      .chip--city {
        padding: 6px 8px;
        gap: 4px;
      }
    }
    `

    this._rootEl = document.createElement('div')
    this._rootEl.className = 'bar'
    this._shadow.append(style, this._rootEl)

    if (this.theme === 'dark') this._shadow.host.setAttribute('data-theme', 'dark')
    if (this.theme === 'light') this._shadow.host.setAttribute('data-theme', 'light')
  }

  _pinIcon(size = '18px') {
    return `<svg xmlns="http://www.w3.org/2000/svg" height="${size}" width="${size}" viewBox="0 -960 960 960" fill="currentColor"><path d="M480-480q66 0 113-47t47-113q0-66-47-113t-113-47q-66 0-113 47t-47 113q0 66 47 113t113 47ZM480-80Q319-217 239.5-334.5T160-552q0-150 96.5-239T480-880q127 0 223.5 89T800-552q0 100-79.5 217.5T480-80Z"/></svg>`
  }

  _cityIcon(city, size = '18px') {
    if (city?.icono_svg) {
      const encoded = encodeURIComponent(city.icono_svg)
      return `<img width="${size}" height="${size}" src="data:image/svg+xml,${encoded}" style="display:inline-block;vertical-align:middle;" />`
    }
    return this._pinIcon(size)
  }

  _countryFlagImg(icoSvg, size = '18px', className = '') {
    const sizeStyle = `width:${size};height:${size};display:inline-block;vertical-align:middle;flex-shrink:0;`
    if (icoSvg) {
      // Inyectar SVG inline, quitar fills hardcodeados y forzar currentColor
      const inlined = icoSvg
        .replace(/^[\s\S]*?(<svg[\s\S]*<\/svg>)[\s\S]*$/, '$1')
        .replace(/\s*fill="[^"]*"/g, '')
        .replace(/\s*fill='[^']*'/g, '')
        .replace(
          /<svg/,
          `<svg fill="currentColor" style="${sizeStyle}" ${className ? `class="${className}"` : ''}`,
        )
      return inlined
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" height="${size}" width="${size}" viewBox="0 -960 960 960" fill="currentColor" ${className ? `class="${className}"` : ''} style="${sizeStyle}opacity:0.6;"><path d="M200-120v-680h360l16 80h224v400H520l-16-80H280v280h-80Z"/></svg>`
  }

  _renderContent() {
    if (!this._rootEl) return

    if (this._outsideClickHandler) {
      document.removeEventListener('click', this._outsideClickHandler)
      this._outsideClickHandler = null
    }

    const {
      cities,
      currentCitySlug,
      currentTenants,
      selectedTenant,
      countries,
      selectedCountry,
      openCityDropdown,
      cityLabel,
    } = this.options
    // En vista de país sin cantón: abrir dropdown automáticamente al renderizar
    if (openCityDropdown) this._dropdownOpen = true
    this._rootEl.classList.toggle('needs-selection', Boolean(openCityDropdown))
    // Filtrar ciudades por país seleccionado
    const filteredCities = selectedCountry
      ? (cities || []).filter(c => c.countryIso2 === selectedCountry)
      : cities || []

    const hasCities = filteredCities && filteredCities.length > 0
    const dropdownItems = hasCities
      ? filteredCities
          .map(
            city => `
        <button
          class="dropdown-item ${city.slug === currentCitySlug ? 'active' : ''}"
          data-slug="${city.slug}"
          data-name="${city.name}">
          ${this._pinIcon('18px')}
          ${city.name}
        </button>
      `,
          )
          .join('')
      : ''

    // País seleccionado
    const hasCountries = countries && countries.length > 0
    const selectedCountryObj = selectedCountry
      ? countries?.find(c => c.iso2 === selectedCountry)
      : null
    const countryDropdownItems = hasCountries
      ? `
        ${countries
          .map(
            country => `
        <button class="country-item ${country.iso2 === selectedCountry ? 'active' : ''}" data-iso2="${country.iso2}">
          ${this._countryFlagImg(country.icono_svg, '18px', 'country-flag')}
          <span class="country-label">${country.iso2}</span>
        </button>
        `,
          )
          .join('')}
      `
      : ''

    const hasTenants = currentTenants && currentTenants.length > 0
    const selectedTenantObj = selectedTenant ? currentTenants.find(t => t.slug === selectedTenant) : null
    const selectedTenantName = selectedTenantObj?.name || (selectedTenant ? 'Filtrar' : 'Todos')
    const selectedTenantIsGeneric = selectedTenantObj?.isGeneric || false
    const tenantDropdownItems = hasTenants
      ? `
        ${
          currentTenants.length > 1
            ? `
        <button class="tenant-item tenant-item--all ${!selectedTenant ? 'active' : ''}" data-slug="">
          <svg class="tenant-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="currentColor"><path d="M480-480q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 66-47 113t-113 47ZM160-160v-112q0-34 17.5-62.5T224-378q62-31 126-46.5T480-440q66 0 130 15.5T736-378q29 15 46.5 43.5T800-272v112H160Z"/></svg>
          <span class="tenant-label">Todos (${currentTenants.length} inst.)</span>
        </button>
        `
            : ''
        }
        ${currentTenants
          .map(
            tenant => `
        <button class="tenant-item ${tenant.slug === selectedTenant ? 'active' : ''} ${tenant.isGeneric ? 'tenant-item--generic' : ''}" data-slug="${tenant.slug}" data-name="${tenant.name}">
          <svg class="tenant-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="currentColor"><path d="M120-120v-560h240v-80l120-120 120 120v240h240v400H120Zm80-80h80v-80h-80v80Zm0-160h80v-80h-80v80Zm0-160h80v-80h-80v80Zm240 320h80v-80h-80v80Zm0-160h80v-80h-80v80Zm0-160h80v-80h-80v80Zm0-160h80v-80h-80v80Zm240 480h80v-80h-80v80Zm0-160h80v-80h-80v80Z"/></svg>
          <span class="tenant-label">${tenant.name}</span>
          ${tenant.isGeneric ? '<span class="tenant-badge-generic">Temporal</span>' : ''}
        </button>
        `,
          )
          .join('')}
      `
      : ''

    this._rootEl.innerHTML = `
      ${
        hasCountries
          ? `
      <div class="country-selector">
        <span
          class="chip chip--country ${countries.length > 1 ? 'chip--clickable' : ''} ${this._countryDropdownOpen ? 'open' : ''}"
          id="countryChip"
          role="${countries.length > 1 ? 'button' : 'status'}"
          tabindex="${countries.length > 1 ? '0' : '-1'}"
          title="${countries.length > 1 ? 'Filtrar por país' : selectedCountryObj?.iso2 || countries[0]?.iso2 || ''}">
          ${this._countryFlagImg(selectedCountryObj?.icono_svg || countries[0]?.icono_svg, '18px')}
          <span class="country-iso">${selectedCountryObj?.iso2 || countries[0]?.iso2 || ''}</span>
          ${countries.length > 1 ? '<svg class="chevron" xmlns="http://www.w3.org/2000/svg" height="14px" viewBox="0 -960 960 960" width="14px" fill="currentColor"><path d="M480-344 240-584l56-56 184 184 184-184 56 56-240 240Z"/></svg>' : ''}
        </span>
        ${countries.length > 1 ? `<div class="country-dropdown ${this._countryDropdownOpen ? 'open' : ''}" id="countryDropdown"><div class="dropdown-header">País</div>${countryDropdownItems}</div>` : ''}
      </div>
      `
          : ''
      }

      <div class="city-selector">
        <span
          class="chip chip--city chip--clickable ${this._dropdownOpen ? 'open' : ''}"
          id="cityChip"
          role="button"
          tabindex="0"
          title="${hasCities ? 'Seleccionar ciudad' : 'Centrar mapa en ' + (this.cityName || this.options.cityLabel || 'la ciudad')}">
          ${this._pinIcon()}
          <span class="name">${this.cityName || this.options.cityLabel || 'Zona'}</span>
          ${hasCities ? '<svg class="chevron" xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor"><path d="M480-344 240-584l56-56 184 184 184-184 56 56-240 240Z"/></svg>' : ''}
        </span>
        ${hasCities ? `<div class="dropdown ${this._dropdownOpen ? 'open' : ''}" id="cityDropdown">${cityLabel ? `<div class="dropdown-header">${cityLabel}</div>` : ''}${dropdownItems}</div>` : ''}
      </div>

      ${
        hasTenants
          ? `
      <div class="tenant-selector">
        <span
          class="chip chip--tenant ${currentTenants.length > 1 ? 'chip--clickable' : ''} ${this._tenantDropdownOpen ? 'open' : ''} ${selectedTenantIsGeneric ? 'chip--generic' : ''}"
          id="tenantChip"
          role="${currentTenants.length > 1 ? 'button' : 'status'}"
          tabindex="${currentTenants.length > 1 ? '0' : '-1'}"
          title="${currentTenants.length > 1 ? 'Filtrar por institución' : selectedTenantName}">
          <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor"><path d="M120-120v-560h240v-80l120-120 120 120v240h240v400H120Zm80-80h80v-80h-80v80Zm0-160h80v-80h-80v80Zm0-160h80v-80h-80v80Zm240 320h80v-80h-80v80Zm0-160h80v-80h-80v80Zm0-160h80v-80h-80v80Zm0-160h80v-80h-80v80Zm240 480h80v-80h-80v80Zm0-160h80v-80h-80v80Z"/></svg>
          <span class="tenant-name">${selectedTenantName}</span>
          ${currentTenants.length > 1 ? '<svg class="chevron" xmlns="http://www.w3.org/2000/svg" height="14px" viewBox="0 -960 960 960" width="14px" fill="currentColor"><path d="M480-344 240-584l56-56 184 184 184-184 56 56-240 240Z"/></svg>' : ''}
        </span>
        <div class="tenant-dropdown ${this._tenantDropdownOpen ? 'open' : ''}" id="tenantDropdown">
          <div class="tenant-dropdown-header">Institución</div>
          ${tenantDropdownItems}
        </div>
      </div>
      `
          : ''
      }
    `

    const cityChip = this._rootEl.querySelector('#cityChip')
    const dropdown = this._rootEl.querySelector('#cityDropdown')

    if (cityChip) {
      if (hasCities && dropdown) {
        cityChip.addEventListener('click', e => {
          e.stopPropagation()
          this._closeTenantDropdown()
          this._closeCountryDropdown()
          this._toggleDropdown()
        })
        cityChip.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            this._closeTenantDropdown()
            this._closeCountryDropdown()
            this._toggleDropdown()
          }
        })

        const items = dropdown.querySelectorAll('.dropdown-item')
        items.forEach(item => {
          item.addEventListener('click', e => {
            e.stopPropagation()
            const slug = item.dataset.slug
            if (slug && this.options.onCityChange) this.options.onCityChange(slug)
            this._closeDropdown()
          })
        })

        this._outsideClickHandler = e => {
          if (!e.composedPath().includes(this._wrapper)) {
            // No cerrar el dropdown de cantón si aún no hay cantón seleccionado
            if (!this.options.openCityDropdown) this._closeDropdown()
            this._closeTenantDropdown()
            this._closeCountryDropdown()
          }
        }
        document.addEventListener('click', this._outsideClickHandler)
      } else {
        const handler = () => this._centerToInitial()
        cityChip.addEventListener('click', handler)
        cityChip.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handler()
          }
        })
      }
    }

    this._setupTenantDropdownListeners()
    this._setupCountryDropdownListeners()
  }

  _setupTenantDropdownListeners() {
    const tenantChip = this._rootEl.querySelector('#tenantChip')
    const tenantDropdown = this._rootEl.querySelector('#tenantDropdown')

    if (tenantChip && tenantDropdown) {
      tenantChip.addEventListener('click', e => {
        e.stopPropagation()
        this._closeDropdown()
        this._toggleTenantDropdown()
      })
      tenantChip.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          this._closeDropdown()
          this._toggleTenantDropdown()
        }
      })

      const tenantItems = tenantDropdown.querySelectorAll('.tenant-item')
      tenantItems.forEach(item => {
        item.addEventListener('click', e => {
          e.stopPropagation()
          const slug = item.dataset.slug
          if (this.options.onTenantChange) this.options.onTenantChange(slug || null)
          this._closeTenantDropdown()
        })
      })

      if (!this._outsideClickHandler) {
        this._outsideClickHandler = e => {
          if (!e.composedPath().includes(this._wrapper)) {
            if (!this.options.openCityDropdown) this._closeDropdown()
            this._closeTenantDropdown()
            this._closeCountryDropdown()
          }
        }
        document.addEventListener('click', this._outsideClickHandler)
      }
    }
  }

  _toggleDropdown() {
    this._dropdownOpen = !this._dropdownOpen
    this._rootEl
      .querySelector('#cityDropdown')
      ?.classList.toggle('open', this._dropdownOpen)
    this._rootEl.querySelector('#cityChip')?.classList.toggle('open', this._dropdownOpen)
  }

  _closeDropdown() {
    this._dropdownOpen = false
    this._rootEl.querySelector('#cityDropdown')?.classList.remove('open')
    this._rootEl.querySelector('#cityChip')?.classList.remove('open')
  }

  _toggleTenantDropdown() {
    this._tenantDropdownOpen = !this._tenantDropdownOpen
    this._rootEl
      .querySelector('#tenantDropdown')
      ?.classList.toggle('open', this._tenantDropdownOpen)
    this._rootEl
      .querySelector('#tenantChip')
      ?.classList.toggle('open', this._tenantDropdownOpen)
  }

  _closeTenantDropdown() {
    this._tenantDropdownOpen = false
    this._rootEl.querySelector('#tenantDropdown')?.classList.remove('open')
    this._rootEl.querySelector('#tenantChip')?.classList.remove('open')
  }

  _setupCountryDropdownListeners() {
    const countryChip = this._rootEl.querySelector('#countryChip')
    const countryDropdown = this._rootEl.querySelector('#countryDropdown')

    if (countryChip && countryDropdown) {
      countryChip.addEventListener('click', e => {
        e.stopPropagation()
        this._closeDropdown()
        this._closeTenantDropdown()
        this._toggleCountryDropdown()
      })
      countryChip.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          this._closeDropdown()
          this._closeTenantDropdown()
          this._toggleCountryDropdown()
        }
      })

      const countryItems = countryDropdown.querySelectorAll('.country-item')
      countryItems.forEach(item => {
        item.addEventListener('click', e => {
          e.stopPropagation()
          const iso2 = item.dataset.iso2
          if (this.options.onCountryChange) this.options.onCountryChange(iso2 || null)
          this._closeCountryDropdown()
        })
      })

      if (!this._outsideClickHandler) {
        this._outsideClickHandler = e => {
          if (!e.composedPath().includes(this._wrapper)) {
            if (!this.options.openCityDropdown) this._closeDropdown()
            this._closeTenantDropdown()
            this._closeCountryDropdown()
          }
        }
        document.addEventListener('click', this._outsideClickHandler)
      }
    }
  }

  _toggleCountryDropdown() {
    this._countryDropdownOpen = !this._countryDropdownOpen
    this._rootEl
      .querySelector('#countryDropdown')
      ?.classList.toggle('open', this._countryDropdownOpen)
    this._rootEl
      .querySelector('#countryChip')
      ?.classList.toggle('open', this._countryDropdownOpen)
  }

  _closeCountryDropdown() {
    this._countryDropdownOpen = false
    this._rootEl.querySelector('#countryDropdown')?.classList.remove('open')
    this._rootEl.querySelector('#countryChip')?.classList.remove('open')
  }

  _renderTenantSelector() {
    if (!this._rootEl) return
    const { currentTenants, selectedTenant } = this.options
    if (!currentTenants?.length) {
      this._rootEl.querySelector('.tenant-selector')?.remove()
      return
    }

    const tenantChip = this._rootEl.querySelector('#tenantChip')
    if (tenantChip) {
      const selectedTenantObj = selectedTenant ? currentTenants.find(t => t.slug === selectedTenant) : null
      const selectedTenantName = selectedTenantObj?.name || (selectedTenant ? 'Filtrar' : 'Todos')
      const nameSpan = tenantChip.querySelector('.tenant-name')
      if (nameSpan) nameSpan.textContent = selectedTenantName
      tenantChip.classList.toggle('chip--generic', Boolean(selectedTenantObj?.isGeneric))
    }

    const tenantDropdown = this._rootEl.querySelector('#tenantDropdown')
    if (tenantDropdown) {
      tenantDropdown.querySelectorAll('.tenant-item').forEach(item => {
        const itemSlug = item.dataset.slug
        const isActive = itemSlug === '' ? !selectedTenant : itemSlug === selectedTenant
        item.classList.toggle('active', isActive)
      })
    }
  }

  _centerToInitial() {
    if (!this._map || !this.options.initialView) return
    const { center, zoom } = this.options.initialView
    this._map.easeTo({ center, zoom, duration: 800, easing: t => 1 - Math.pow(1 - t, 3) })
  }
}

export default CityStatusBarControl
